import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronDown,
  Plus,
  Presentation,
  TrendingUp,
  ClipboardCheck,
  ClipboardList,
  Users,
  Award,
  Puzzle,
  BookOpen,
  FileText,
  Compass,
  Wrench,
  User,
  Layers,
} from "lucide-react";
import { BRAND, ROUTES } from "@/config/links";
import { useLang } from "@/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { trackCta } from "@/lib/analytics";

const trackBookClick = (ctaId) => trackCta({ ctaId, location: "nav", href: ROUTES.booking, targetService: "general_booking" });

// Line icons for mega-menu cards only — desktop, decorative, subtle (no
// colour, single stroke weight) — matches the InterAct line-sketch
// aesthetic rather than generic colourful SaaS iconography. Never used
// on mobile, which keeps its existing plain-text accordion untouched.
const MEGA_ICONS = {
  highstakes: Presentation,
  epd: TrendingUp,
  audit: ClipboardCheck,
  pce: Users,
  epl: Award,
  bespoke: Puzzle,
  articles: BookOpen,
  casestudies: FileText,
  guides: Compass,
  tools: Wrench,
  research: ClipboardList,
  aboutus: User,
  methodology: Layers,
};

/**
 * Site-wide top navigation.
 * - Desktop/laptop (>=1024px, `lg:`): 5 primary items — Coaching, Corporate,
 *   L&D, Resources, About. Items with a submenu (all except L&D) open a
 *   full-width mega-menu panel on click (see the panel block below); L&D
 *   links straight to its page since it has no submenu.
 * - Mobile (<1024px): unchanged plain accordion — same SUB data, ignoring
 *   the desktop-only `description`/icon fields.
 * All labels come from the translation dictionary and every internal href
 * is localized through useLang().l() so /pt/* and /el/* paths are preserved.
 */
export const Nav = () => {
  const { t, l, lang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // "coaching" | "corporate" | "insights" | "about" | null
  const [mobileOpenSub, setMobileOpenSub] = useState(null); // mobile accordion — one submenu open at a time, independent of desktop's openMenu
  const headerRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenMenu(null);
    setMobileOpenSub(null);
  }, [pathname]);

  // Collapse the mobile accordion back to fully-closed every time the
  // mobile menu itself closes, so re-opening it always starts fresh.
  useEffect(() => {
    if (!open) setMobileOpenSub(null);
  }, [open]);

  // Body scroll-lock while the mobile menu is open: body-only
  // `overflow:hidden` isn't reliable across browsers because the actual
  // scrolling root is often <html>, not <body> — a wheel/touch event
  // that doesn't land squarely on the inner scroll container would
  // still scroll the page behind the fixed overlay. The position:fixed
  // technique locks the true viewport regardless of which element is
  // the scroll root, and restores the exact scroll position on close.
  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const body = document.body;
    const previous = { position: body.style.position, top: body.style.top, width: body.style.width, overflow: body.style.overflow };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.width = previous.width;
      body.style.overflow = previous.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  // Outside-click + Escape close the open mega menu (desktop only — the
  // mobile accordion has its own toggle button and isn't affected).
  useEffect(() => {
    if (!openMenu) return;
    const onPointerDown = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) setOpenMenu(null);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openMenu]);

  // Each entry keeps the exact same shape mobile always used (key, label,
  // to, badge?, muted?) plus two desktop-only additions — `description`
  // and an icon looked up from MEGA_ICONS by `key`. Mobile simply never
  // reads those two fields, so its accordion is byte-for-byte unchanged.
  const SUB = {
    coaching: [
      { key: "highstakes", label: t("nav.sub_high_stakes"), to: ROUTES.highStakesPresentationCoaching, description: t("nav.desc_high_stakes") },
      { key: "epd",        label: t("nav.sub_epd"),         to: ROUTES.executivePresenceDevelopment,   description: t("nav.desc_epd") },
      { key: "audit",      label: t("nav.sub_free_audit"),  to: ROUTES.nextPresentationReadinessCheck, badge: t("nav.free_badge"), description: t("nav.desc_free_audit"), targetAssetId: "next_presentation_readiness" },
      { key: "all",        label: t("nav.sub_all_coaching"), to: ROUTES.executiveCoaching, muted: true, description: t("nav.desc_all_coaching") },
    ],
    corporate: [
      { key: "pce",     label: t("nav.sub_pce"),           to: ROUTES.presentationExcellence,      description: t("nav.desc_pce") },
      { key: "epl",     label: t("nav.sub_epl"),           to: ROUTES.executivePresenceForLeaders, description: t("nav.desc_epl") },
      { key: "bespoke", label: t("nav.sub_bespoke"),       to: ROUTES.customPrograms,              description: t("nav.desc_bespoke") },
      { key: "all",     label: t("nav.sub_all_corporate"), to: ROUTES.corporateTraining, muted: true, description: t("nav.desc_all_corporate") },
    ],
    insights: [
      { key: "audit",       label: t("nav.sub_free_audit"),    to: ROUTES.nextPresentationReadinessCheck, badge: t("nav.free_badge"), description: t("nav.desc_free_audit"), targetAssetId: "next_presentation_readiness" },
      { key: "articles",    label: t("nav.sub_articles"),      to: ROUTES.articles,     description: t("nav.desc_articles") },
      { key: "casestudies", label: t("nav.sub_case_studies"),  to: ROUTES.caseStudies,  description: t("nav.desc_case_studies") },
      { key: "guides",      label: t("nav.sub_guides"),        to: ROUTES.guides,       description: t("nav.desc_guides") },
      { key: "tools",       label: t("nav.sub_tools"),         to: ROUTES.tools,        description: t("nav.desc_tools") },
      { key: "research",    label: t("nav.sub_research"),      to: ROUTES.workplaceCommunicationResearch, description: t("nav.desc_research") },
      { key: "all",         label: t("nav.sub_all_resources"), to: ROUTES.insights, muted: true, description: t("nav.desc_all_resources") },
    ],
    about: [
      { key: "aboutus",     label: t("nav.sub_about_us"),   to: ROUTES.about,        description: t("nav.desc_about_us") },
      { key: "methodology", label: t("nav.methodology"),    to: "/how-we-work",      description: t("nav.desc_methodology") },
    ],
  };

  // Exactly 5 primary items, in this order. Home was removed (the logo
  // already links home); Methodology moved inside About's submenu.
  const SITE_LINKS = [
    { key: "coaching",  label: t("nav.coaching"),  to: ROUTES.executiveCoaching, sub: "coaching"  },
    { key: "corporate", label: t("nav.corporate"), to: ROUTES.corporateTraining, sub: "corporate" },
    { key: "ld",        label: t("nav.ld"),        to: ROUTES.ldPartnerships },
    { key: "insights",  label: t("nav.resources"), to: ROUTES.insights, sub: "insights" },
    { key: "about",     label: t("nav.about"),     to: ROUTES.about, sub: "about" },
  ];

  const localPath = pathname.replace(/^\/(pt|el)(?=\/|$)/, "") || "/";

  const toggleMenu = (key) => setOpenMenu((cur) => (cur === key ? null : key));

  const linkClick = (parentKey, s) => () => {
    setOpenMenu(null);
    trackCta({ ctaId: `nav-${parentKey}-sub-${s.key}`, location: `${parentKey}_dropdown`, href: s.to, targetAssetId: s.targetAssetId });
  };

  return (
    <motion.header
      ref={headerRef}
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || openMenu ? "bg-paper/95 backdrop-blur-md border-b border-ink/10" : "bg-transparent"
      }`}
      data-testid="site-nav"
      lang={lang}
    >
      <nav className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to={l("/")} data-testid="nav-logo" className="flex flex-col leading-none">
          <span className="font-serif text-xl md:text-2xl tracking-tight text-ink">
            {BRAND.company}
          </span>
          <span className="font-hand text-terracotta text-base md:text-lg -mt-1">
            {t("brand.tagline_hand")}
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {SITE_LINKS.map((li) => {
            const isActive = localPath.startsWith(li.to) && li.to !== "/";
            if (li.sub) {
              const isOpen = openMenu === li.sub;
              return (
                <button
                  key={li.key}
                  type="button"
                  data-testid={`nav-${li.key}`}
                  aria-haspopup="true"
                  aria-expanded={isOpen}
                  onClick={() => toggleMenu(li.sub)}
                  className={`font-body text-sm uppercase tracking-[0.14em] transition-colors duration-300 inline-flex items-center gap-1.5 ${
                    isActive || isOpen ? "text-terracotta" : "text-ink-soft hover:text-terracotta"
                  }`}
                >
                  {li.label}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} strokeWidth={2} />
                </button>
              );
            }
            return (
              <Link
                key={li.key}
                to={l(li.to)}
                data-testid={`nav-${li.key}`}
                className={`font-body text-sm uppercase tracking-[0.14em] transition-colors duration-300 ${
                  isActive ? "text-terracotta" : "text-ink-soft hover:text-terracotta"
                }`}
              >
                {li.label}
              </Link>
            );
          })}
          <LanguageSwitcher variant="desktop" />
          <Link
            to={l(ROUTES.booking)}
            data-testid="nav-book-cta"
            onClick={trackBookClick.bind(null, "nav-book-cta")}
            className="bg-ink text-paper px-6 py-3 font-body text-xs uppercase tracking-[0.16em] hover:bg-terracotta transition-colors duration-300"
          >
            {t("cta.book_consultation")}
          </Link>
        </div>

        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label={t("nav.toggle_menu")}
          className="lg:hidden flex flex-col gap-1.5 p-2"
        >
          <span className={`block w-6 h-[2px] bg-ink transition-transform duration-300 ${open ? "translate-y-[7px] rotate-45" : ""}`} />
          <span className={`block w-6 h-[2px] bg-ink transition-opacity duration-300 ${open ? "opacity-0" : ""}`} />
          <span className={`block w-6 h-[2px] bg-ink transition-transform duration-300 ${open ? "-translate-y-[7px] -rotate-45" : ""}`} />
        </button>
      </nav>

      {/* Desktop/laptop mega menu — full-width panel overlaying the page
          directly under the header; absolutely positioned so it never
          affects document flow/height, so opening/closing it never shifts
          page content. lg:+ only, click-triggered (not hover). */}
      <AnimatePresence>
        {openMenu && SUB[openMenu] && (
          <motion.div
            key={openMenu}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="hidden lg:block absolute top-full left-0 right-0 bg-paper border-b border-ink/10 shadow-[0_28px_56px_rgba(27,27,24,0.12)]"
            data-testid={`nav-megamenu-${openMenu}`}
            role="region"
            aria-label={`${SITE_LINKS.find((x) => x.sub === openMenu)?.label ?? ""} menu`}
          >
            <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10">
              <div
                className={`grid gap-x-10 gap-y-8 ${
                  openMenu === "about"
                    ? "grid-cols-2 max-w-md"
                    : openMenu === "insights"
                    ? "grid-cols-3 xl:grid-cols-5"
                    : "grid-cols-3"
                }`}
              >
                {SUB[openMenu].filter((s) => !s.muted).map((s) => {
                  const Icon = MEGA_ICONS[s.key];
                  return (
                    <Link
                      key={s.key}
                      to={l(s.to)}
                      onClick={linkClick(openMenu, s)}
                      data-testid={`nav-${openMenu}-sub-${s.key}`}
                      className="group flex flex-col gap-3 text-left"
                    >
                      {Icon && (
                        <span className="w-10 h-10 rounded-full border border-ink/15 flex items-center justify-center text-terracotta group-hover:border-terracotta group-hover:bg-terracotta/5 transition-colors duration-200 shrink-0">
                          <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
                        </span>
                      )}
                      <span className="flex flex-col gap-1.5">
                        <span className="flex items-center gap-2 font-serif text-base text-ink group-hover:text-terracotta transition-colors duration-200">
                          {s.label}
                          {s.badge && (
                            <span
                              data-testid={`nav-${openMenu}-sub-${s.key}-badge`}
                              className="font-mono text-[9px] uppercase tracking-[0.16em] text-terracotta border border-terracotta/40 px-2 py-0.5 shrink-0"
                            >
                              {s.badge}
                            </span>
                          )}
                        </span>
                        {s.description && (
                          <span className="font-body text-xs text-ink-soft leading-relaxed">{s.description}</span>
                        )}
                      </span>
                    </Link>
                  );
                })}
              </div>
              {SUB[openMenu].filter((s) => s.muted).map((s) => (
                <div key={s.key} className="mt-8 pt-6 border-t border-ink/10">
                  <Link
                    to={l(s.to)}
                    onClick={linkClick(openMenu, s)}
                    data-testid={`nav-${openMenu}-sub-${s.key}`}
                    className="font-body text-xs uppercase tracking-[0.16em] italic text-ink-soft hover:text-terracotta transition-colors duration-200"
                  >
                    {s.label} &rarr;
                  </Link>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile accordion — unchanged design, <lg only. Inner content gets
          its own scroll boundary so tall menus never get clipped on short
          screens and never chain-scroll the page behind them. */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-paper border-b border-ink/10"
            data-testid="nav-mobile-menu"
          >
            <div
              className="px-6 pt-6 pb-[max(80px,env(safe-area-inset-bottom))] flex flex-col gap-4 max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain"
              data-testid="nav-mobile-menu-scroll"
            >
              {SITE_LINKS.map((li) => {
                const isSubOpen = mobileOpenSub === li.key;
                return (
                  <div key={li.key} className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-3">
                      <Link
                        to={l(li.to)}
                        onClick={() => setOpen(false)}
                        data-testid={`nav-mobile-${li.key}`}
                        className="font-serif text-2xl text-ink"
                      >
                        {li.label}
                      </Link>
                      {li.sub && (
                        <button
                          type="button"
                          onClick={() => setMobileOpenSub((cur) => (cur === li.key ? null : li.key))}
                          aria-expanded={isSubOpen}
                          aria-controls={`nav-mobile-${li.key}-submenu`}
                          aria-label={`${li.label} ${t("nav.toggle_menu")}`}
                          data-testid={`nav-mobile-${li.key}-toggle`}
                          className="flex items-center justify-center w-10 h-10 shrink-0 text-ink"
                        >
                          <Plus
                            className={`w-5 h-5 transition-transform duration-300 ${isSubOpen ? "rotate-45" : ""}`}
                            strokeWidth={2}
                          />
                        </button>
                      )}
                    </div>
                    {li.sub && (
                      <AnimatePresence initial={false}>
                        {isSubOpen && (
                          <motion.div
                            key="submenu"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                            className="overflow-hidden"
                            id={`nav-mobile-${li.key}-submenu`}
                            data-testid={`nav-mobile-${li.key}-submenu`}
                          >
                            <div className="pl-4 flex flex-col gap-2 border-l border-ink/15 pb-1">
                              {SUB[li.sub].map((s) => (
                                <Link
                                  key={s.key}
                                  to={l(s.to)}
                                  onClick={() => {
                                    setOpen(false);
                                    trackCta({ ctaId: `nav-mobile-${li.key}-sub-${s.key}`, location: `${li.key}_dropdown`, href: s.to, targetAssetId: s.targetAssetId });
                                  }}
                                  data-testid={`nav-mobile-${li.key}-sub-${s.key}`}
                                  className={`flex items-center gap-3 font-body text-base ${s.muted ? "text-ink-soft italic" : "text-ink-soft hover:text-terracotta"}`}
                                >
                                  <span>{s.label}</span>
                                  {s.badge && (
                                    <span
                                      data-testid={`nav-mobile-${li.key}-sub-${s.key}-badge`}
                                      className="font-mono text-[9px] uppercase tracking-[0.16em] text-terracotta border border-terracotta/40 px-2 py-0.5"
                                    >
                                      {s.badge}
                                    </span>
                                  )}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    )}
                  </div>
                );
              })}
              <div className="pt-2">
                <LanguageSwitcher variant="mobile" />
              </div>
              <Link
                to={l(ROUTES.booking)}
                onClick={() => { setOpen(false); trackBookClick("nav-mobile-book-cta"); }}
                data-testid="nav-mobile-book-cta"
                className="mt-2 bg-ink text-paper px-6 py-4 text-center font-body text-sm uppercase tracking-[0.16em]"
              >
                {t("cta.book_consultation")}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
